import { useEffect, useState } from "react";
import { Users, CheckCircle, Clock, DollarSign, Copy, Check, Share2, User, UserPlus } from "lucide-react";
import { toast } from "sonner";
import "./Referrals.css";
import { fetchReferralInfoUrl } from "../../../api/endpoints";
import WithdrawFunds from "./WithdrawFunds";
import { setUser } from "../../../slice/webStoreUserSlice";
import { useDispatch } from "react-redux";

// Progress Step Component
const ReferralProgressStep = ({ label, status, isLast }) => {
  const getIcon = () => {
    switch (status) {
      case "completed":
        return <CheckCircle className='ref-progress-step-icon-completed' size={20} />;
      case "in-progress":
        return <Clock className='ref-progress-step-icon-progress' size={20} />;
      default:
        return <Clock className='ref-progress-step-icon-pending' size={20} />;
    }
  };

  const getLabelClass = () => {
    switch (status) {
      case "completed":
        return "ref-progress-step-label ref-progress-step-label-completed";
      case "in-progress":
        return "ref-progress-step-label ref-progress-step-label-progress";
      default:
        return "ref-progress-step-label ref-progress-step-label-pending";
    }
  };

  return (
    <div className='ref-progress-step'>
      <div className='ref-progress-step-icon-wrapper'>
        {getIcon()}
        {!isLast && (
          <div className={`ref-progress-step-line ${status === "completed" ? "ref-progress-step-line-completed" : "ref-progress-step-line-default"}`} />
        )}
      </div>
      <div className={getLabelClass()}>{label}</div>
    </div>
  );
};

// Referral Card Component
const ReferralCard = ({ referral, currency }) => {
  const allStepsCompleted = Object.values(referral.steps).every((step) => step === "completed");

  return (
    <div className='ref-card'>
      <div className='ref-card-content'>
        <div className='ref-card-header'>
          <div className='ref-card-user-info'>
            <div className='ref-card-avatar'>
              <User size={16} />
            </div>
            <div className='ref-card-user-details'>
              <h3 className='ref-card-user-name'>{referral.name}</h3>
              <p className='ref-card-user-email'>{referral.email}</p>
              <p className='ref-card-user-date'>Joined {referral.joinedDate}</p>
            </div>
          </div>
          {/* referral.steps.createProduct === "completed" */}
          {referral.payout && (
            <div className='ref-card-payout'>
              <div className='ref-card-payout-amount'>
                <DollarSign size={16} />
                <span>
                  {currency}
                  {referral.payout.amount}
                </span>
              </div>
              <div className={`ref-card-payout-badge ${referral.payout.status === "paid" ? "ref-card-payout-badge-paid" : "ref-card-payout-badge-pending"}`}>
                {referral.steps.createProduct === "completed" ? (referral.payout.status === "paid" ? "Paid" : "Pending") : " Incomplete"}
              </div>
            </div>
          )}
        </div>

        <div className='ref-card-progress-section'>
          <p className='ref-card-progress-label'>Progress</p>
          <ReferralProgressStep label='Sign Up' status={referral.steps.signup} />
          <ReferralProgressStep label='Subscribe' status={referral.steps.subscription} />
          <ReferralProgressStep label='Create Store' status={referral.steps.createStore} />
          <ReferralProgressStep label='Add Product' status={referral.steps.createProduct} isLast={true} />
        </div>

        {allStepsCompleted && !referral.payout && <div className='ref-card-awaiting-badge'>Awaiting payout</div>}
      </div>
    </div>
  );
};

// Referral List Component
const ReferralList = ({ referrals, currency }) => {
  const convertedReferrals = convertReferrals(referrals);

  return (
    <div className='ref-list-section'>
      <h2 className='ref-list-title'>Your Referrals</h2>
      {convertedReferrals.length > 0 ? (
        <div className='ref-list-grid'>
          {convertedReferrals.map((referral) => (
            <ReferralCard key={referral.id} referral={referral} currency={currency} />
          ))}
        </div>
      ) : (
        <div className='ref-empty-state'>
          <div className='ref-empty-state-icon'>
            <UserPlus size={48} />
          </div>
          <h3 className='ref-empty-state-title'>No referrals yet</h3>
          <p className='ref-empty-state-description'>Start sharing your referral link to invite friends and earn rewards!</p>
        </div>
      )}
    </div>
  );
};

// Referral Link Card Component
const ReferralLinkCard = ({ referralLink }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on this amazing platform!",
          text: "Sign up using my referral link and we both get rewards!",
          url: referralLink,
        });
      } catch (error) {
        // User cancelled share
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className='ref-link-card'>
      <div className='ref-link-content'>
        <div>
          <h3 className='ref-link-header-title'>Your Referral Link</h3>
          <p className='ref-link-header-description'>Share this link with friends and earn rewards when they complete their journey!</p>
        </div>

        <div className='ref-link-input-group'>
          <input value={referralLink} readOnly className='ref-link-input' />
          <button onClick={handleCopy} className='ref-link-button ref-link-button-outline'>
            {copied ? <Check className='ref-link-icon-success' size={16} /> : <Copy size={16} />}
          </button>
          <button onClick={handleShare} className='ref-link-button ref-link-button-primary'>
            <Share2 size={16} />
          </button>
        </div>

        <div className='ref-link-info-box'>
          <h4 className='ref-link-info-title'>How it works:</h4>
          <ol className='ref-link-info-list'>
            <li>Share your unique referral link</li>
            <li>Your friend signs up and subscribes</li>
            <li>They create a store and add products</li>
            <li>You get rewarded when they complete the journey!</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

// Stats Card Component
const StatCard = ({ icon, label, value, trend }) => (
  <div className='ref-stat-card'>
    <div className='ref-stat-card-content'>
      <div className='ref-stat-card-info'>
        <p className='ref-stat-label'>{label}</p>
        <p className='ref-stat-value'>{value}</p>
        {trend && <p className='ref-stat-trend'>{trend}</p>}
      </div>
      <div className='ref-stat-icon-wrapper'>{icon}</div>
    </div>
  </div>
);

// Referral Stats Component
const ReferralStats = ({ stats, currency }) => {
  return (
    <div className='ref-stats-grid'>
      <StatCard icon={<Users size={20} />} label='Total Referrals' value={stats.referralsCount} trend={`+${stats.referredThisMonth} this month`} />
      <StatCard icon={<CheckCircle size={20} />} label='Completed' value={stats.completedSteps} />
      <StatCard icon={<Clock size={20} />} label='In Progress' value={stats.referralInProgress} />
      <StatCard
        icon={<DollarSign size={20} />}
        label='Total Earned'
        value={`${currency + stats.totalEarnings}`}
        trend={`+${currency + stats.pendingPayouts} to be paid`}
      />
    </div>
  );
};

// Main Referrals Page Component
const ReferralsProgram = () => {
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [referralUrl, setReferralUrl] = useState("");
  const [currency, setCurrency] = useState("USD");

  useEffect(() => {
    fetchReferralData();
  }, []);
  const dispatch = useDispatch();

  const fetchReferralData = async () => {
    try {
      let res = await fetch(fetchReferralInfoUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch referral data");

      let { user, data, currency, referrals } = await res.json();
      console.log(user, referrals);

      setReferralData(data);
      setReferrals(referrals || []);
      setReferralUrl(user.referralLink || "");
      setCurrency(currency);
      dispatch(setUser(user));
    } catch (error) {
      console.error("Error fetching referral data:", error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className='ref-dashboard-container'>
      {loading ? (
        <ReferralProgramSkeleton />
      ) : (
        <div className='ref-dashboard-wrapper'>
          <div className='ref-dashboard-content'>
            {/* Header */}
            <div className='ref-header-section'>
              <h1 className='ref-header-title privacy-data-info-title'>Referral Program</h1>
              <p className='ref-header-subtitle'>Earn rewards by inviting friends to join the platform</p>
            </div>

            {/* Stats */}
            <ReferralStats stats={referralData} currency={currency} />

            {/* Referral Link Card */}
            <ReferralLinkCard referralLink={referralUrl} />

            {/* Referrals List */}
            <ReferralList referrals={referrals} currency={currency} />
            {/* withdraw funds */}
            <WithdrawFunds referralData={referralData} currency={currency} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralsProgram;

const ReferralProgramSkeleton = () => {
  const skeletonStyle = {
    backgroundColor: "#e0e0e0",
    borderRadius: "8px",
    animation: "pulse 1.5s ease-in-out infinite",
  };

  const pulseKeyframes = `
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `;

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px", fontFamily: "Arial, sans-serif", backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <style>{pulseKeyframes}</style>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <div
          style={{
            ...skeletonStyle,
            height: "36px",
            width: "250px",
            margin: "0 auto 10px",
          }}
        ></div>
        <div
          style={{
            ...skeletonStyle,
            height: "20px",
            width: "400px",
            margin: "0 auto",
          }}
        ></div>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          marginBottom: "40px",
        }}
      >
        {[1, 2, 3, 4].map((item) => (
          <div
            key={item}
            style={{
              backgroundColor: "#fff",
              padding: "24px",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
              <div style={{ ...skeletonStyle, height: "16px", width: "100px" }}></div>
              <div style={{ ...skeletonStyle, height: "40px", width: "40px", borderRadius: "50%" }}></div>
            </div>
            <div style={{ ...skeletonStyle, height: "40px", width: "60px", marginBottom: "12px" }}></div>
            <div style={{ ...skeletonStyle, height: "14px", width: "80px" }}></div>
          </div>
        ))}
      </div>

      {/* Referral Link Section */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          marginBottom: "40px",
        }}
      >
        <div style={{ ...skeletonStyle, height: "24px", width: "180px", marginBottom: "12px" }}></div>
        <div style={{ ...skeletonStyle, height: "16px", width: "500px", marginBottom: "20px" }}></div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            backgroundColor: "#f9fafb",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <div style={{ ...skeletonStyle, height: "20px", flex: 1 }}></div>
          <div style={{ ...skeletonStyle, height: "40px", width: "40px", borderRadius: "8px" }}></div>
          <div style={{ ...skeletonStyle, height: "40px", width: "40px", borderRadius: "8px" }}></div>
        </div>

        <div style={{ backgroundColor: "#f9fafb", padding: "16px", borderRadius: "8px" }}>
          <div style={{ ...skeletonStyle, height: "18px", width: "120px", marginBottom: "16px" }}></div>
          {[1, 2, 3, 4].map((item) => (
            <div key={item} style={{ ...skeletonStyle, height: "16px", width: "100%", marginBottom: "10px" }}></div>
          ))}
        </div>
      </div>

      {/* Referrals List */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ ...skeletonStyle, height: "28px", width: "150px", marginBottom: "20px" }}></div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "20px" }}>
          {[1, 2].map((item) => (
            <div
              key={item}
              style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <div style={{ ...skeletonStyle, height: "40px", width: "40px", borderRadius: "50%" }}></div>
                  <div>
                    <div style={{ ...skeletonStyle, height: "20px", width: "120px", marginBottom: "8px" }}></div>
                    <div style={{ ...skeletonStyle, height: "14px", width: "180px", marginBottom: "6px" }}></div>
                    <div style={{ ...skeletonStyle, height: "12px", width: "100px" }}></div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ ...skeletonStyle, height: "20px", width: "60px", marginBottom: "8px", marginLeft: "auto" }}></div>
                  <div style={{ ...skeletonStyle, height: "24px", width: "70px", borderRadius: "12px", marginLeft: "auto" }}></div>
                </div>
              </div>

              <div style={{ marginTop: "16px" }}>
                <div style={{ ...skeletonStyle, height: "14px", width: "80px", marginBottom: "12px" }}></div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ ...skeletonStyle, height: "20px", width: "20px", borderRadius: "50%" }}></div>
                  <div style={{ ...skeletonStyle, height: "16px", width: "100px" }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function convertReferrals(referrals) {
  return referrals.map((ref) => {
    const joinedDate = new Date(ref.signUpDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    // Ordered steps
    const stepsOrder = ["signup", "subscription", "createStore", "createProduct"];

    // Map backend progress to step index
    const progressMap = {
      signUp: 0,
      subscribed: 1,
      createStore: 2,
      addedProduct: 3,
      pending: -1, // no progress yet
    };

    const currentStepIndex = progressMap[ref.progress] ?? -1;

    // Build steps with 3-state logic
    const steps = {};
    stepsOrder.forEach((step, index) => {
      if (index < currentStepIndex) {
        steps[step] = "completed";
      } else if (index === currentStepIndex) {
        steps[step] = "completed"; // because progress indicates completion of that step
      } else if (index === currentStepIndex + 1) {
        steps[step] = "in-progress";
      } else {
        steps[step] = "pending";
      }
    });

    // Extract payout info
    const subInfo = ref.clientSubscriptionInfo || {};
    const payout = {
      amount: subInfo.potentialPayoutToReferrer || 0,
      status: subInfo.isPayoutMade ? "paid" : "unpaid",
    };

    return {
      id: String(ref._id),
      name: ref.clientName,
      email: ref.clientEmail,
      joinedDate,
      steps,
      payout,
    };
  });
}
